import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Center, HStack, ShoppingCartOutline, Text, VStack } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { AnalyticsEvent, useAnalytics } from '../../../lib/analytics';
import { useDialogControl } from '../../../lib/dialog';
import { assertPresence } from '../../../lib/runtime';
import {
	getActiveCollectibleItem,
	usePostPurchaseCollectibleModal,
} from '../../../modules/rewards/reward-details-modal/components/post-purchase-collectible-modal';
// TODO: Move this to the rewards components file during refactor
import { createRewardsIndexParamsSchema } from '../../../modules/rewards/rewards-index/utils';
import { loadRewards, useHighlights } from '../../../queries/rewards';
import { DistributionMethod } from '../../../schemas/rewards';
import { NoActivityEmptyState } from './no-activity-empty-state';
import { RewardsHighlightsSection } from './rewards-highlights-section';

const MAX_PROMOTED = 5;
const MAX_FOR_YOU = 8;

export interface RewardsHighlightsContentProps {
	onShopAllClick?: () => void;
}

interface ErrorStateProps {
	onRetry: () => void;
}

const ErrorState: FC<ErrorStateProps> = ({ onRetry }) => {
	const { t } = useTranslation('rewards');

	return (
		<Center p={6} borderWidth="1px" borderColor="border.subtle" borderRadius="md" bg="bg.subtle">
			<VStack spacing={3}>
				<Text color="text.primary" fontSize="sm" fontWeight="medium">
					{t('highlights.errorLoading')}
				</Text>
				<Box
					as="button"
					onClick={onRetry}
					px={4}
					py={2}
					borderRadius="md"
					bg="bg.brand.primary"
					color="text.on-brand"
					fontSize="sm"
					fontWeight="medium"
					_hover={{ bg: 'bg.brand.primary-hover' }}
					cursor="pointer"
				>
					{t('highlights.retry')}
				</Box>
			</VStack>
		</Center>
	);
};

export const RewardsHighlightsContent: FC<RewardsHighlightsContentProps> = ({ onShopAllClick }) => {
	const { t } = useTranslation('rewards');
	const { t: c } = useTranslation('common');
	const { data, isError, refetch } = useHighlights();
	const rewardModal = useDialogControl('reward');
	const collectibleModal = usePostPurchaseCollectibleModal();
	const { track } = useAnalytics();
	const currentUser = useCurrentUser();

	// Preload rewards page data when panel opens
	useEffect(() => {
		const schema = createRewardsIndexParamsSchema();
		const params = schema.parse({});
		loadRewards(params);
	}, []);

	const handleRewardClick = (rewardId: string) => {
		track(AnalyticsEvent.Rewards.ClickViewReward, { source: 'widget' });
		const allRewards = [...(data?.promoted ?? []), ...(data?.recent ?? [])];
		const reward = allRewards.find((r) => r.id === rewardId);
		if (reward?.distributionMethod === DistributionMethod.Collectible && getActiveCollectibleItem(currentUser)) {
			collectibleModal.onOpen();
		} else {
			rewardModal.onOpen({ rewardId, skipPostPurchase: null });
		}
	};

	if (isError) {
		return <ErrorState onRetry={refetch} />;
	}

	// Data should always be present since highlights are preloaded on hover
	assertPresence(data);

	// Limit items per section
	const promotedItems = data.promoted.slice(0, MAX_PROMOTED);
	const recentItems = data.recent.slice(0, MAX_FOR_YOU);

	// Get user's point balance
	const pointBalance = currentUser?.earning_balance ?? 0;

	return (
		<VStack spacing={4} align="stretch">
			{/* Header with Shop All and balance */}
			{onShopAllClick && (
				<HStack
					as="button"
					onClick={onShopAllClick}
					justify="space-between"
					align="center"
					px={2.5}
					py={2}
					borderRadius="lg"
					_hover={{ bg: 'secondary.bgAlpha' }}
					transition="background 0.2s"
					cursor="pointer"
				>
					<HStack spacing={3}>
						<ShoppingCartOutline boxSize={6} color="text.secondary" />
						<Text fontSize="16px" fontWeight="500" lineHeight="24px" letterSpacing="-0.208px" color="text.secondary">
							{t('highlights.shopAll')}
						</Text>
					</HStack>
					<Box bg="points.bgAlpha" px={1.5} py={0.75} borderRadius="full">
						<Text fontSize="xs" fontWeight="semibold" color="points.fg">
							{c('intl_number', { value: pointBalance })}
						</Text>
					</Box>
				</HStack>
			)}

			{/* Always show For You section first, with empty state if needed */}
			<RewardsHighlightsSection
				title={t('highlights.forYou')}
				rewards={recentItems}
				onRewardClick={handleRewardClick}
				emptyState={recentItems.length === 0 ? <NoActivityEmptyState /> : undefined}
			/>

			{/* Only show promoted section if items exist */}
			{promotedItems.length > 0 && <RewardsHighlightsSection title={t('highlights.promoted')} rewards={promotedItems} onRewardClick={handleRewardClick} />}
		</VStack>
	);
};
